import { useState } from 'react';
import { BUILD_STAMP } from '../lib/api';
import { ApiFailure, api, type PillarCode, type SessionUser } from '../lib/api';

interface Props {
  onAuthenticated: (user: SessionUser, entityScope: PillarCode | null) => void;
}

/**
 * §0.4: the login screen carries no pillar chrome, because the pillar is not
 * known until the user is. It is navy on navy — never gold on white.
 */
export function Login({ onAuthenticated }: Props): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user, entityScope } = await api.login(email, password);
      onAuthenticated(user, entityScope);
    } catch (failure) {
      // The server returns a single `invalid_credentials` for a wrong password,
      // an unknown address and a deactivated account alike. The UI must not be
      // more helpful than the API, or it becomes the account oracle.
      setError(
        failure instanceof ApiFailure && failure.status === 400
          ? 'Enter a valid email address and password.'
          : 'Those credentials were not accepted.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-navy px-4 py-12">
      <form
        onSubmit={submit}
        className="w-full max-w-[380px] rounded-card bg-white p-6 shadow-lg"
        aria-labelledby="login-heading"
      >
        <p className="text-micro font-medium uppercase text-text-secondary">CALM Companies</p>
        <h1 id="login-heading" className="mb-1 mt-1 text-h1 font-semibold text-text-primary">
          Operations Platform
        </h1>
        <p className="mb-6 text-body text-text-secondary">
          Sign in with your CALM operations account.
        </p>

        <label className="mb-1 block text-micro font-medium uppercase text-text-secondary" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4 h-touch w-full rounded-card border border-border bg-white px-3 text-body text-text-primary"
        />

        <label className="mb-1 block text-micro font-medium uppercase text-text-secondary" htmlFor="password">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-5 h-touch w-full rounded-card border border-border bg-white px-3 text-body text-text-primary"
        />

        {error ? (
          <p role="alert" className="mb-4 text-body text-danger">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={busy}
          className="btn-primary h-touch w-full rounded-card bg-royal text-body font-semibold text-white hover:bg-royal-hover disabled:opacity-60"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {/*
          Printed on the card itself so a screenshot of this screen identifies
          which build the browser is running. A cached bundle reporting an old
          stamp is otherwise indistinguishable from a genuine auth failure.
        */}
        <p className="mt-4 text-center text-xs text-slate-400">build {BUILD_STAMP}</p>
      </form>
    </main>
  );
}
